import type {
  ButtonHTMLAttributes,
  AnchorHTMLAttributes,
  CSSProperties,
  InputHTMLAttributes,
  PropsWithChildren,
  ReactNode,
} from 'react';
import { badgeStyle, buttonStyle, cardStyle, inputStyle, metricCardStyle } from './style-utils.ts';
import type { BadgeVariant, ButtonSize, ButtonVariant } from './style-utils.ts';
import { semanticColor } from '@beautyburst/tokens';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={rest.type ?? 'button'}
      disabled={disabled === true || loading}
      aria-busy={loading ? 'true' : undefined}
      data-bb-component="button"
      data-bb-variant={variant}
      style={{ ...buttonStyle(variant, size), ...(rest.style ?? {}) }}
    >
      {loading ? 'Working…' : children}
    </button>
  );
}

/** A semantic navigation link styled with the same minimum touch target as a button. */
export function LinkButton({
  variant = 'primary',
  size = 'md',
  children,
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
}) {
  return (
    <a
      {...rest}
      data-bb-component="link-button"
      data-bb-variant={variant}
      style={{ ...buttonStyle(variant, size), textDecoration: 'none', ...(rest.style ?? {}) }}
    >
      {children}
    </a>
  );
}

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  hint?: string;
  error?: string;
}

/** Accessible text field: programmatic label, hint and error association. */
export function Field({ id, label, hint, error, required, ...rest }: FieldProps) {
  const hintId = hint === undefined ? undefined : `${id}-hint`;
  const errorId = error === undefined ? undefined : `${id}-error`;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div data-bb-component="field" style={{ display: 'grid', gap: '0.375rem' }}>
      <label
        htmlFor={id}
        style={{ fontSize: '0.875rem', fontWeight: 600, color: semanticColor.textPrimary }}
      >
        {label}
        {required === true ? (
          <span aria-hidden="true" style={{ color: semanticColor.statusDanger }}>
            {' *'}
          </span>
        ) : null}
      </label>
      {hint === undefined ? null : (
        <span id={hintId} style={{ fontSize: '0.8125rem', color: semanticColor.textSecondary }}>
          {hint}
        </span>
      )}
      <input
        {...rest}
        id={id}
        required={required}
        aria-invalid={error === undefined ? undefined : 'true'}
        aria-describedby={describedBy}
        style={{ ...inputStyle(error !== undefined), ...(rest.style ?? {}) }}
      />
      {error === undefined ? null : (
        <span
          id={errorId}
          role="alert"
          style={{ fontSize: '0.8125rem', color: semanticColor.statusDangerText }}
        >
          {error}
        </span>
      )}
    </div>
  );
}

export function Card({ children, ...rest }: PropsWithChildren<{ id?: string }>) {
  return (
    <section {...rest} data-bb-component="card" style={cardStyle()}>
      {children}
    </section>
  );
}

export interface BadgeProps {
  variant?: BadgeVariant;
  children: ReactNode;
  /** Screen-reader text when the visible label is an abbreviation. */
  srLabel?: string;
}

export function Badge({ variant = 'neutral', children, srLabel }: BadgeProps) {
  return (
    <span
      data-bb-component="badge"
      data-bb-variant={variant}
      style={{ ...badgeStyle(variant), maxWidth: '100%', minWidth: 0, overflow: 'hidden' }}
    >
      {srLabel === undefined ? null : (
        <span style={{ position: 'absolute', left: '-9999px' }}>{srLabel}</span>
      )}
      {children}
    </span>
  );
}

export interface SellerBadgeProps {
  /** Retailer display name, or null for Beauty Burst Direct. */
  retailerName: string | null;
}

/** Seller transparency is mandatory on every product surface. */
export function SellerBadge({ retailerName }: SellerBadgeProps) {
  const label = retailerName === null ? 'Sold by Beauty Burst' : `Sold by ${retailerName}`;
  return (
    <Badge variant="seller">
      <span
        data-bb-seller-label
        aria-label={label}
        title={label}
        style={{
          display: 'block',
          minWidth: 0,
          maxWidth: '100%',
          // Seller transparency is mandatory, so the name wraps to a second line
          // in a narrow card rather than being clipped to an ellipsis. Long
          // single words still break instead of widening the card.
          overflowWrap: 'anywhere',
        }}
      >
        {label}
      </span>
    </Badge>
  );
}

export type AvailabilityState =
  'OPEN_NOW' | 'OPEN_LATE' | 'OVERNIGHT' | 'TWENTY_FOUR_HOUR' | 'CLOSED';

const AVAILABILITY_LABEL: Record<AvailabilityState, string> = {
  OPEN_NOW: 'Open now',
  OPEN_LATE: 'Open late',
  OVERNIGHT: 'Available overnight',
  TWENTY_FOUR_HOUR: '24 hours',
  CLOSED: 'Closed',
};

const AVAILABILITY_VARIANT: Record<AvailabilityState, BadgeVariant> = {
  OPEN_NOW: 'openNow',
  OPEN_LATE: 'openLate',
  OVERNIGHT: 'overnight',
  TWENTY_FOUR_HOUR: 'overnight',
  CLOSED: 'outOfStock',
};

/** Availability labels render only when operationally valid. */
export function AvailabilityBadge({ state }: { state: AvailabilityState }) {
  return <Badge variant={AVAILABILITY_VARIANT[state]}>{AVAILABILITY_LABEL[state]}</Badge>;
}

export type StockState = 'IN_STOCK' | 'LOW' | 'CRITICAL' | 'OUT_OF_STOCK';

const STOCK_LABEL: Record<StockState, string> = {
  IN_STOCK: 'In stock',
  LOW: 'Low stock',
  CRITICAL: 'Critical stock',
  OUT_OF_STOCK: 'Out of stock',
};

const STOCK_VARIANT: Record<StockState, BadgeVariant> = {
  IN_STOCK: 'inStock',
  LOW: 'lowStock',
  CRITICAL: 'criticalStock',
  OUT_OF_STOCK: 'outOfStock',
};

export function StockBadge({ state }: { state: StockState }) {
  return <Badge variant={STOCK_VARIANT[state]}>{STOCK_LABEL[state]}</Badge>;
}

export interface MetricCardProps {
  label: string;
  value: string;
  helpText?: string;
}

/** Operational metric card. Retailer and admin surfaces only. */
export function MetricCard({ label, value, helpText }: MetricCardProps) {
  return (
    <div data-bb-component="metric-card" style={metricCardStyle()}>
      <p
        style={{
          margin: 0,
          fontSize: '0.8125rem',
          color: semanticColor.textSecondary,
          fontWeight: 600,
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: '0.25rem 0 0',
          fontSize: '1.5rem',
          color: semanticColor.textPrimary,
          fontWeight: 700,
        }}
      >
        {value}
      </p>
      {helpText === undefined ? null : (
        <p
          style={{
            margin: '0.25rem 0 0',
            fontSize: '0.8125rem',
            color: semanticColor.textSecondary,
          }}
        >
          {helpText}
        </p>
      )}
    </div>
  );
}

/**
 * Heading level for the state primitives. These render as `h3` inside a page
 * that already has its own `h1`, but when one of them replaces the whole page
 * (an unrecoverable load, or an empty portal screen) that page has no `h1` at
 * all, which fails the single top-level-heading expectation for the screen.
 * Page-level usages pass `headingLevel={1}`.
 */
export type StateHeadingLevel = 1 | 2 | 3;

function StateHeading({
  level,
  style,
  children,
}: {
  level: StateHeadingLevel;
  style: CSSProperties;
  children: ReactNode;
}) {
  if (level === 1) return <h1 style={style}>{children}</h1>;
  if (level === 2) return <h2 style={style}>{children}</h2>;
  return <h3 style={style}>{children}</h3>;
}

export interface EmptyStateProps {
  title: string;
  description: string;
  action?: ReactNode;
  headingLevel?: StateHeadingLevel;
}

export function EmptyState({ title, description, action, headingLevel = 3 }: EmptyStateProps) {
  return (
    <div
      data-bb-component="empty-state"
      style={{
        ...cardStyle(),
        display: 'grid',
        justifyItems: 'center',
        gap: '0.5rem',
        minHeight: '12rem',
        textAlign: 'center',
      }}
    >
      <span
        aria-hidden="true"
        data-bb-empty-state-mark
        style={{
          color: semanticColor.brandContrast,
          fontSize: '0.6875rem',
          fontWeight: 800,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
        }}
      >
        Beauty Burst
      </span>
      <StateHeading
        level={headingLevel}
        style={{ margin: 0, fontSize: '1.125rem', color: semanticColor.textPrimary }}
      >
        {title}
      </StateHeading>
      <p style={{ margin: '0.5rem 0 1rem', color: semanticColor.textSecondary }}>{description}</p>
      {action === undefined ? null : <div data-bb-empty-state-action>{action}</div>}
    </div>
  );
}

export function LoadingState({ label = 'Loading' }: { label?: string }) {
  return (
    <div
      data-bb-component="loading-state"
      role="status"
      aria-live="polite"
      aria-label={label}
      style={{
        ...cardStyle(),
        display: 'grid',
        gap: '0.75rem',
        minHeight: '9rem',
        overflow: 'hidden',
      }}
    >
      <span className="bb-visually-hidden">{label}</span>
      <span aria-hidden="true" data-bb-skeleton="heading" />
      <span aria-hidden="true" data-bb-skeleton="line" />
      <span aria-hidden="true" data-bb-skeleton="line-short" />
    </div>
  );
}

export function ErrorState({
  title,
  description,
  retry,
  headingLevel = 3,
}: {
  title: string;
  description: string;
  retry?: ReactNode;
  headingLevel?: StateHeadingLevel;
}) {
  return (
    <div
      data-bb-component="error-state"
      role="alert"
      style={{ ...cardStyle(), borderColor: semanticColor.statusDanger }}
    >
      <StateHeading
        level={headingLevel}
        style={{ margin: 0, fontSize: '1.125rem', color: semanticColor.statusDangerText }}
      >
        {title}
      </StateHeading>
      <p style={{ margin: '0.5rem 0 1rem', color: semanticColor.textPrimary }}>{description}</p>
      {retry}
    </div>
  );
}

export interface DataTableColumn<Row> {
  key: string;
  header: string;
  render: (row: Row) => ReactNode;
  numeric?: boolean;
}

export interface DataTableProps<Row> {
  caption: string;
  columns: readonly DataTableColumn<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  emptyMessage?: string;
}

/** Operational table foundation for retailer and admin surfaces. */
export function DataTable<Row>({
  caption,
  columns,
  rows,
  rowKey,
  emptyMessage,
}: DataTableProps<Row>) {
  if (rows.length === 0) {
    return <EmptyState title={caption} description={emptyMessage ?? 'Nothing to show yet.'} />;
  }
  return (
    // maxWidth/minWidth pin the scroll container to its parent's width. As a
    // grid or flex item it would otherwise default to min-width:auto, grow to
    // the table's max-content width, and its own overflowX would never engage.
    <div
      data-bb-component="data-table"
      style={{ overflowX: 'auto', maxWidth: '100%', minWidth: 0 }}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <caption
          style={{
            captionSide: 'top',
            textAlign: 'left',
            padding: '0 0 0.5rem',
            color: semanticColor.textSecondary,
          }}
        >
          {caption}
        </caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                style={{
                  textAlign: column.numeric === true ? 'right' : 'left',
                  background: semanticColor.surfaceSubtle,
                  color: semanticColor.textSecondary,
                  fontSize: '0.8125rem',
                  padding: '0.75rem 1rem',
                }}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)}>
              {columns.map((column) => (
                <td
                  key={column.key}
                  style={{
                    borderTop: `1px solid ${semanticColor.borderDefault}`,
                    padding: '0.75rem 1rem',
                    fontSize: '0.875rem',
                    textAlign: column.numeric === true ? 'right' : 'left',
                  }}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
